import { defineStore } from "pinia";
import {
  PromosCMSParams,
  PromosOption,
  PromosOutputParams,
  PromosSettings,
  VacationType,
} from "@/../types/promos-types";
import { computed, ref } from "vue";
import { Option } from "@/../types/option";
import { getPromosParamsFromURL } from "@/utils/promos-utils";
import { fetchDurationsData } from "@/services/promos-ms-api";
import { useI18n } from "vue-i18n";

const mapPromosOptionsToOptions = (items?: PromosOption[]): Option[] => {
  return (
    items?.map((item) => ({
      value: item.code,
      label: item.label,
      sr: item.sr,
    })) ?? []
  );
};

const getStarRatingOptions = (
  t: (key: string, params?: { star: string }) => string
): PromosOption[] => {
  return [
    {
      code: "2",
      label: t("star-rating.and-up"),
      sr: t("star-rating.stars-sr", { star: "2" }),
    },
    {
      code: "3",
      label: t("star-rating.and-up"),
      sr: t("star-rating.stars-sr", { star: "3" }),
    },
    {
      code: "4",
      label: t("star-rating.and-up"),
      sr: t("star-rating.stars-sr", { star: "4" }),
    },
    {
      code: "5",
      label: "",
      sr: t("star-rating.5-stars-sr"),
    },
  ];
};

export const usePromosSettingsStore = defineStore("promos-settings", () => {
  const { t } = useI18n();

  // State
  const promosSettings = ref<PromosSettings | undefined>();
  const isDurationsLoading = ref<boolean>(false);
  const isDurationInitialized = ref<boolean>(false);
  const allowedDurations = ref<string[]>([]);

  // Getters
  const gatewaysOptions = computed<Option[]>((): Option[] => {
    return mapPromosOptionsToOptions(promosSettings.value?.gatewayOptions);
  });

  const destinationOptions = computed<Option[]>((): Option[] => {
    return mapPromosOptionsToOptions(promosSettings.value?.destinationOptions);
  });

  const monthOptions = computed<Option[]>((): Option[] => {
    return mapPromosOptionsToOptions(promosSettings.value?.monthOptions);
  });

  const durationOptions = computed<Option[]>((): Option[] => {
    const filteredDurations =
      allowedDurations.value.length > 0
        ? promosSettings.value?.durationOptions?.filter((duration) =>
            allowedDurations.value.includes(duration)
          )
        : promosSettings.value?.durationOptions;

    return (
      filteredDurations?.map((duration) => ({
        value: duration,
        label: duration,
      })) ?? []
    );
  });

  const starRatingOptions = computed<Option[]>((): Option[] => {
    return mapPromosOptionsToOptions(promosSettings.value?.starRatingOptions);
  });

  const vacationType = computed<VacationType>((): VacationType => {
    return promosSettings.value?.vacationType || VacationType.PACKAGE;
  });

  const isTour = computed((): boolean => {
    return vacationType.value === VacationType.TOUR;
  });

  const promosParams = computed<PromosOutputParams>((): PromosOutputParams => {
    return {
      year_month:
        promosSettings.value?.month ||
        promosSettings.value?.monthOptions[0]?.code,
      gateway:
        promosSettings.value?.gateway ||
        promosSettings.value?.gatewayOptions[0]?.code,
      destination:
        promosSettings.value?.destination ||
        promosSettings.value?.destinationOptions[0]?.code,
      duration:
        promosSettings.value?.duration ||
        promosSettings.value?.durationOptions[0],
      starRating: promosSettings.value?.starRating ?? "2",
      lang: promosSettings.value?.promoSearchLang,
      promo_start_date: promosSettings.value?.promoTravelStart,
      promo_end_date: promosSettings.value?.promoTravelEnd,
      promotion_id: promosSettings.value?.promoId,
      targeted_hotel_list: promosSettings.value?.targetedHotelList,
      alias: promosSettings.value?.alias,
      priceCap: promosSettings.value?.priceCap,
    };
  });

  // Actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function setPromosSettings(data: PromosCMSParams) {
    const paramsFromUrl = getPromosParamsFromURL(window.location.href, data);
    const sortedDurationOptions = [...data.durations];
    sortedDurationOptions.sort((a, b) => parseInt(a) - parseInt(b));

    promosSettings.value = {
      destination: data.defaultDestination,
      gateway: data.defaultGateway,
      month: data.defaultDepartureMonth,
      duration: data.defaultDuration,
      starRating: "2",
      targetedHotelList: data.targetedHotelList,
      alias: data.alias,
      promoSearchLang: data.promoSearchLang,
      promoBookingStart: data.promo_booking_start,
      promoBookingEnd: data.promo_booking_end,
      promoId: data.promo_id,
      promoPath: data.promo_path,
      promoTravelStart: data.promo_travel_start,
      promoTravelEnd: data.promo_travel_end,
      vacationType: data.vacationType,
      destinationOptions: data.destinations,
      durationOptions: sortedDurationOptions,
      gatewayOptions: data.gateways,
      monthOptions: data.months,
      priceCap: data.priceCap,
      starRatingOptions: getStarRatingOptions(t),
      ...paramsFromUrl,
    };
  }

  function setMonth(value: string) {
    if (promosSettings.value) {
      promosSettings.value.month = value;
      fetchDurations();
    }
  }

  function setGateway(value: string) {
    if (promosSettings.value) {
      promosSettings.value.gateway = value;
      fetchDurations();
    }
  }

  function setDestination(value: string) {
    if (promosSettings.value) {
      promosSettings.value.destination = value;
      fetchDurations();
    }
  }

  function setDuration(value: string) {
    if (promosSettings.value) {
      promosSettings.value.duration = value;
    }
  }

  function setStarRating(value: string) {
    if (promosSettings.value) {
      promosSettings.value.starRating = value;
    }
  }

  function validateDuration() {
    if (allowedDurations.value.length > 0 && promosSettings.value?.duration) {
      if (!allowedDurations.value.includes(promosSettings.value.duration)) {
        promosSettings.value.duration = allowedDurations.value[0];
      }
    }
  }

  async function fetchDurations() {
    if (!isTour.value) {
      isDurationsLoading.value = true;
      const tokenValue = await window.sso.getUserToken();
      if (tokenValue instanceof Error) {
        throw tokenValue;
      }
      try {
        const response: Response = await fetchDurationsData(
          promosParams.value.gateway ?? "",
          promosParams.value.destination ?? "",
          promosParams.value.year_month ?? "",
          tokenValue
        );
        if (response.ok) {
          const data = (await response.json()) as { durations: number[] };
          allowedDurations.value = data.durations?.map((duration) =>
            duration.toString()
          );
        }
        isDurationsLoading.value = false;
        isDurationInitialized.value = true;
      } catch (e) {
        allowedDurations.value = [];
        isDurationsLoading.value = false;
      }
      validateDuration();
    }
  }

  return {
    promosSettings,
    promosParams,
    setPromosSettings,
    setMonth,
    setGateway,
    setDestination,
    setDuration,
    setStarRating,
    gatewaysOptions,
    destinationOptions,
    monthOptions,
    durationOptions,
    starRatingOptions,
    isTour,
    isDurationsLoading,
    isDurationInitialized,
    fetchDurations,
    allowedDurations,
    validateDuration,
    vacationType,
  };
});
